import { atom } from "jotai";
import { z } from "zod";

import { CreateAuth } from "./CreateAuth";
import { JotaiToolExecute } from "../../index";

/**
 * Parameters for creating authentication for an API
 */
export const CreateAuthParams = z.object({
  selected_api: z
    .string()
    .describe(
      "REQUIRED: The API identifier to create authentication for (e.g., 'GoogleCalendarCLIAPI@1.5.0' or 'gmail'). This is a required parameter for the tool. If you don't know this, don't guess. Fetch it via the implementations-meta/search api using the 'term' parameter.",
    ),
});

export type CreateAuthResult = {
  success: boolean;
  message: string;
  auth_id?: string;
  redirect_url?: string;
};

const description = `🚨 CRITICAL: BUTTON TOOL - ENDS RESPONSE IMMEDIATELY 🚨

Creates authentication for a specified API.

## MANDATORY BEHAVIOR:
- This tool shows a button and MUST be the LAST tool call in any response
- After calling this tool, DO NOT output any additional text or call any other tools
- The response MUST end immediately to prevent UX conflicts

## Use this tool when:
- User wants to set up authentication for an API

## This tool will:
- Render a button to the chat window that will open a popup to create a new auth connection
- Uses /implementations-meta/search endpoint with 'term' parameter (NEVER /implementations which returns all implementations)

## CRITICAL RULES:
- NEVER use with continue_button or auth_confirmation_button in the same response
- NEVER output text after calling this tool - button must be at end of message
- NEVER call manage_todos or any other tool after this tool
- RESPONSE MUST END IMMEDIATELY after this tool call
`;

export const createAuthAtom = atom(() => ({
  name: "create_auth",
  description,
  parameters: CreateAuthParams,
  component: CreateAuth,
  execute: createAuthExecute,
}));

const createAuthExecute: JotaiToolExecute<z.infer<typeof CreateAuthParams>> =
  atom(null, async (_get, _set, { args }) => {
    let params;

    try {
      params = CreateAuthParams.parse(args || {});
    } catch (error) {
      return {
        $error: {
          message: `Error: Invalid arguments provided for create_auth. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      };
    }

    const { selected_api } = params;

    try {
      // The tool shows a UI component that handles the actual auth creation
      // This allows users to click a button to create the connection
      const result: CreateAuthResult = {
        success: true,
        message: `Showing authentication setup for ${selected_api}. Click the button below to create the connection.`,
      };

      return {
        content: [
          {
            type: "text",
            text: result.message,
          },
        ],
        // Mark as waiting for user input to prevent continue button
        waitingForUser: true,
      };
    } catch (error) {
      const errorResult: CreateAuthResult = {
        success: false,
        message: `Failed to create authentication: ${error instanceof Error ? error.message : "Unknown error"}`,
      };

      return {
        $error: {
          message: `Error creating authentication: ${JSON.stringify(errorResult, null, 2)}`,
        },
      };
    }
  });
